import { MemberStatus, SubscriptionStatus } from '@/types/member.types';

/**
 * Single home for the payment-failure 7/8/15-day policy. Consumers
 * (MembershipStatusBanner, MemberAccessGuard) read `paymentFailure` off
 * `useMemberAccess()` rather than re-deriving these thresholds.
 */
export const GRACE_PERIOD_DAYS = 7;

export type PaymentFailurePhase = 'none' | 'grace' | 'suspended' | 'ended';

export interface PaymentFailureState {
  phase: PaymentFailurePhase;
  /** 0..GRACE_PERIOD_DAYS, meaningful only when phase === 'grace'. */
  daysRemaining: number;
}

const MS_PER_DAY = 86_400_000;

export function derivePaymentFailureState(input: {
  status?: MemberStatus;
  subscriptionStatus?: SubscriptionStatus;
  subscriptionStatusChangedAt?: string;
  now?: Date;
}): PaymentFailureState {
  const { status, subscriptionStatus, subscriptionStatusChangedAt } = input;
  const now = input.now ?? new Date();

  const isSuspendedStatus = status === MemberStatus.SUSPENDED;
  const isCancelledSubscription =
    subscriptionStatus === SubscriptionStatus.CANCELLED;
  const isCancelledOrSuspendedStatus =
    status === MemberStatus.CANCELLED || isSuspendedStatus;

  // `ended` — subscription cancelled while the member is suspended/cancelled.
  if (isCancelledSubscription && isCancelledOrSuspendedStatus) {
    return { phase: 'ended', daysRemaining: 0 };
  }

  // Explicit backend suspension wins over the day count.
  if (isSuspendedStatus) {
    return { phase: 'suspended', daysRemaining: 0 };
  }

  if (subscriptionStatus === SubscriptionStatus.PAYMENT_FAILED) {
    const daysElapsed = computeDaysElapsed(subscriptionStatusChangedAt, now);

    // Missing anchor must never escalate a member into a block wall.
    if (daysElapsed === undefined) {
      return { phase: 'grace', daysRemaining: GRACE_PERIOD_DAYS };
    }

    if (daysElapsed >= GRACE_PERIOD_DAYS + 1) {
      // Day 8 hardens even if GHL has not yet flipped membership status.
      return { phase: 'suspended', daysRemaining: 0 };
    }

    return {
      phase: 'grace',
      daysRemaining: Math.max(0, GRACE_PERIOD_DAYS - daysElapsed),
    };
  }

  return { phase: 'none', daysRemaining: 0 };
}

function computeDaysElapsed(
  changedAt: string | undefined,
  now: Date
): number | undefined {
  if (!changedAt) {
    return undefined;
  }

  const changedAtDate = new Date(changedAt);
  if (Number.isNaN(changedAtDate.getTime())) {
    return undefined;
  }

  const diffMs = now.getTime() - changedAtDate.getTime();
  return Math.max(0, Math.floor(diffMs / MS_PER_DAY));
}
